import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMechanicAuth } from '../MechanicAuthContext';
import axios from 'axios';
import { Plus, Search, Wrench } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: '', label: 'Tous' },
  { key: 'open', label: 'Ouverts' },
  { key: 'in_progress', label: 'En cours' },
  { key: 'completed', label: 'Completes' },
  { key: 'cancelled', label: 'Annulés' },
];

const STATUS_COLORS = {
  open: 'bg-blue-500/15 text-blue-400',
  in_progress: 'bg-amber-500/15 text-amber-400',
  completed: 'bg-emerald-500/15 text-emerald-400',
  cancelled: 'bg-red-500/15 text-red-400',
};

const STATUS_LABELS = { open: 'Ouvert', in_progress: 'En cours', completed: 'Complete', cancelled: 'ANNULÉ' };

const TASK_LABELS = {
  diagnostic: 'Diag', reparation: 'Rep', entretien: 'Entr',
  inspection: 'Insp', mesures_pneus: 'Pneus', mesures_freins: 'Freins',
};

export default function MechanicWorkOrdersPage() {
  const { token } = useMechanicAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const activeTab = searchParams.get('status') || '';

  const fetchOrders = useCallback(async () => {
    try {
      const params = activeTab ? `?status=${activeTab}` : '';
      const res = await axios.get(`${API_URL}/api/mechanic/work-orders${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(res.data);
    } catch {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [token, activeTab]);

  useEffect(() => { setLoading(true); fetchOrders(); }, [fetchOrders]);

  const filtered = search
    ? orders.filter(o =>
        [o.order_number, o.vehicle_unit, o.vehicle_plate, o.client_name]
          .filter(Boolean).join(' ').toLowerCase().includes(search.toLowerCase())
      )
    : orders;

  return (
    <div className="p-4 pb-20 space-y-4" data-testid="mechanic-work-orders-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-white">Bons de travail</h1>
        <button
          onClick={() => navigate('/admin/mecanicien/bons/nouveau')}
          className="bg-amber-500 hover:bg-amber-400 text-black font-semibold px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm"
          data-testid="new-order-btn"
        >
          <Plus size={16} /> Nouveau
        </button>
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher..."
          className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-amber-500/50 focus:outline-none"
          data-testid="search-input"
        />
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1 no-scrollbar">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => tab.key ? setSearchParams({ status: tab.key }) : setSearchParams({})}
            className={`px-3.5 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.key ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-400 active:bg-slate-700'
            }`}
            data-testid={`tab-${tab.key || 'all'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 text-center">
          <Wrench size={36} className="mx-auto text-slate-600 mb-2" />
          <p className="text-slate-400 text-sm">Aucun bon de travail</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(order => (
            <button
              key={order.id}
              onClick={() => navigate(`/admin/mecanicien/bons/${order.id}`)}
              className="w-full bg-slate-900 border border-slate-800 rounded-xl p-4 text-left active:scale-[0.98] transition-transform"
              data-testid={`order-card-${order.id}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-white font-semibold text-sm">{order.order_number}</span>
                <div className="flex items-center gap-1.5">
                  {!order.mechanic_id && <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-purple-500/15 text-purple-400">A prendre</span>}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[order.status] || 'bg-slate-500/15 text-slate-400'}`}>
                    {STATUS_LABELS[order.status] || order.status}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-400">
                {order.vehicle_unit && <span>#{order.vehicle_unit}</span>}
                {order.vehicle_plate && <span>{order.vehicle_plate}</span>}
                {order.client_name && <span>{order.client_name}</span>}
              </div>
              {(order.created_by_name || order.mechanic_name) && (
                <p className="text-[10px] text-slate-500 mt-1" data-testid={`order-${order.id}-by`}>
                  Fait par: {order.created_by_name || order.mechanic_name}
                </p>
              )}
              {(order.tasks || []).length > 0 && (
                <div className="flex gap-1 mt-1.5">
                  {order.tasks.map((t, i) => (
                    <span key={i} className="text-[10px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded">
                      {TASK_LABELS[t.type] || t.type}
                    </span>
                  ))}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
